'use client'

import { Button } from '@/components/ui/button'
import { useDictionary } from '@/contexts/DictionaryContext'
import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import { ArrowRight, Mail, MessageCircle, Phone } from 'lucide-react'
import Link from 'next/link'
import { useEffect, useState } from 'react'

interface ContactBannerProps {
  className?: string
}

interface Particle {
  top: number
  left: number
}

export default function ContactBanner({ className }: ContactBannerProps) {
  const [particles, setParticles] = useState<Particle[]>([])
  const { dictionary } = useDictionary()
  const translations = dictionary.common.contactBanner

  useEffect(() => {
    // Generate particle positions only on the client side
    setParticles(
      Array.from({ length: 20 }).map(() => ({
        top: Math.random() * 100,
        left: Math.random() * 100,
      }))
    )
  }, [])

  return (
    <section className={cn('relative overflow-hidden py-16', className)}>
      {/* Background with gradient */}
      <div className='absolute inset-0 bg-linear-to-br from-slate-900 via-blue-900 to-slate-900'>
        {/* Animated particles */}
        <div className='absolute inset-0 opacity-10'>
          {particles.map((particle, i) => (
            <motion.div
              key={i}
              className='absolute h-2 w-2 rounded-full bg-white'
              style={{
                top: `${particle.top}%`,
                left: `${particle.left}%`,
              }}
              animate={{
                y: [0, Math.random() * 30 - 15],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Number.POSITIVE_INFINITY,
                repeatType: 'reverse',
              }}
            />
          ))}
        </div>
      </div>

      <div className='container relative mx-auto px-4'>
        <div className='flex flex-col items-center justify-between gap-8 md:flex-row'>
          <div className='max-w-2xl'>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className='mb-2 inline-block rounded-full bg-blue-500/20 px-4 py-1 text-sm font-medium uppercase tracking-wider text-blue-300'
            >
              <span className='animate-pulse mr-2 inline-block h-2 w-2 rounded-full bg-blue-400'></span>
              {translations.tagline}
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className='text-3xl font-bold tracking-tight text-white md:text-4xl'
            >
              {translations.title.split('Daha İyi Çözümler')[0]}{' '}
              <span className='relative inline-block'>
                <span className='relative z-10'>{translations.title.includes('Daha İyi Çözümler') ? 'Daha İyi Çözümler' : 'Better Solutions'}</span>
                <span className='absolute bottom-0 left-0 z-0 h-3 w-full bg-blue-500/20'></span>
              </span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className='mt-4 text-slate-300'
            >
              {translations.description}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className='mt-6 flex flex-wrap gap-4'
            >
              {[
                { icon: <MessageCircle className='h-4 w-4' />, text: translations.features.support },
                { icon: <Phone className='h-4 w-4' />, text: translations.features.consultation },
                { icon: <Mail className='h-4 w-4' />, text: translations.features.response },
              ].map((item, index) => (
                <div
                  key={index}
                  className='flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-sm text-white backdrop-blur-xs'
                >
                  {item.icon}
                  <span>{item.text}</span>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className='relative z-20'
          >
            <div className='relative'>
              {/* Decorative elements */}
              <div className='absolute -left-6 -top-6 h-20 w-20 rounded-full bg-blue-500/20 blur-xl'></div>
              <div className='absolute -bottom-4 -right-4 h-16 w-16 rounded-full bg-cyan-500/20 blur-xl'></div>

              <Link
                href='/contact'
                className='inline-flex'
              >
                <Button
                  size='lg'
                  className='group relative overflow-hidden bg-linear-to-r from-blue-500 to-cyan-400 text-white transition-all hover:shadow-lg hover:shadow-blue-500/25'
                >
                  <span className='relative z-10 flex items-center font-medium'>
                    {translations.cta} <ArrowRight className='ml-2 h-4 w-4 transition-transform group-hover:translate-x-1' />
                  </span>
                  <span className='absolute inset-0 z-0 bg-linear-to-r from-blue-600 to-cyan-500 opacity-0 transition-opacity group-hover:opacity-100'></span>
                </Button>
              </Link>

              {/* Pulsing circle */}
              <motion.div
                className='absolute -right-4 -top-4 h-8 w-8 rounded-full bg-blue-400/30'
                animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0.2, 0.5] }}
                transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
